import { createFileRoute } from '@tanstack/react-router'
import { pageHead } from '../lib/seo'

const LAST_UPDATED = '2026-05-08'

export const Route = createFileRoute('/terms')({
  head: () =>
    pageHead({
      path: '/terms',
      title: 'Terms of Service',
      description:
        'Terms of Service for HTML Editor — a free online WYSIWYG HTML editor that runs in your browser.'
    }),
  component: TermsPage
})

function TermsPage() {
  return (
    <article className="max-w-3xl mx-auto px-4 py-12">
      <h1 className="text-3xl md:text-4xl font-bold mb-2">Terms of Service</h1>
      <p className="text-sm text-gray-500 mb-8">Last updated: {LAST_UPDATED}</p>

      <p className="mb-6 leading-relaxed">
        These Terms of Service ("Terms") govern your use of the website at
        htmleditor.io (the "Service"), operated by{' '}
        <strong>Digital Rocket Factory Oy</strong> ("we", "us", "our"). By
        using the Service, you agree to these Terms. If you do not agree, do
        not use the Service.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">1. The Service</h2>
      <p className="mb-4 leading-relaxed">
        HTML Editor is a free, browser-based WYSIWYG editor that lets you
        compose HTML content. The Service is provided as-is, without any
        registration, and runs primarily in your browser.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">2. Acceptable use</h2>
      <p className="mb-4 leading-relaxed">
        You agree to use the Service only for lawful purposes. You must not
        use the Service to create, distribute, or facilitate content that is
        illegal, infringing, harmful, or that interferes with the operation of
        the Service or the experience of other users.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">3. Your content</h2>
      <p className="mb-4 leading-relaxed">
        Content you create, paste, or edit in the Service belongs to you. We
        do not claim ownership of your content. The Service runs in your
        browser and we do not store the content of your editor sessions on our
        servers.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">
        4. Intellectual property
      </h2>
      <p className="mb-4 leading-relaxed">
        The Service itself — including its design, code, branding, and the
        HTML Editor name — is the property of Digital Rocket Factory Oy or its
        licensors and is protected by intellectual property laws. You may not
        copy, modify, or redistribute the Service without permission.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">5. No warranty</h2>
      <p className="mb-4 leading-relaxed">
        The Service is provided "as is" and "as available", without warranties
        of any kind, whether express or implied, including warranties of
        merchantability, fitness for a particular purpose, or
        non-infringement. We do not warrant that the Service will be
        uninterrupted, error-free, or secure.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">
        6. Limitation of liability
      </h2>
      <p className="mb-4 leading-relaxed">
        To the maximum extent permitted by applicable law, Digital Rocket
        Factory Oy will not be liable for any indirect, incidental, special,
        consequential, or punitive damages arising out of or related to your
        use of the Service. Our total liability for any claim relating to the
        Service is limited to the amount you have paid us for the Service
        (which, for the free Service, is zero).
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">7. Changes</h2>
      <p className="mb-4 leading-relaxed">
        We may update these Terms from time to time. The "Last updated" date
        above indicates the latest revision. Continued use of the Service
        after changes take effect constitutes acceptance of the updated Terms.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">8. Governing law</h2>
      <p className="mb-4 leading-relaxed">
        These Terms are governed by the laws of Finland. Any disputes that
        cannot be resolved informally will be subject to the exclusive
        jurisdiction of the Finnish courts.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">9. Contact</h2>
      <p className="mb-4 leading-relaxed">
        For questions about these Terms, email{' '}
        <a
          href="mailto:support@htmleditor.io"
          className="text-[#ff694a] underline hover:no-underline"
        >
          support@htmleditor.io
        </a>
        .
      </p>
    </article>
  )
}
